import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../config";
import { ErrorState, ErrorBanner } from "../components/ErrorState";
import { classifyError } from "../lib/apiError";
import { validateWalletAmount } from "../lib/amountUtils";
import {
  Wallet, Plus, Send, Eye, EyeOff, RefreshCw, ArrowDownLeft, ArrowUpRight,
  QrCode, Sparkles, Shield, Loader2, X, ChevronRight
} from "lucide-react";

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const QUICK_AMOUNTS = [5000, 10000, 25000, 50000, 100000];

export default function WalletPage() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [autoPayRules, setAutoPayRules] = useState([]);
  const [securitySettings, setSecuritySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideBalance, setHideBalance] = useState(false);

  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [topUpProcessing, setTopUpProcessing] = useState(false);

  const [showTransfer, setShowTransfer] = useState(false);
  const [transferForm, setTransferForm] = useState({ phone: "", amount: "", note: "" });
  const [transferProcessing, setTransferProcessing] = useState(false);

  const [showAutoTopUp, setShowAutoTopUp] = useState(false);
  const [autoTopUpForm, setAutoTopUpForm] = useState({
    enabled: false,
    threshold: 1000,
    amount: 5000,
  });
  const [autoTopUpSaving, setAutoTopUpSaving] = useState(false);

  const [showTxnPin, setShowTxnPin] = useState(false);
  const [txnPinForm, setTxnPinForm] = useState({ currentPin: "", newPin: "", confirmPin: "" });
  const [txnPinSaving, setTxnPinSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [walletRes, txRes, rulesRes, settingsRes] = await Promise.all([
        api.get("/wallet"),
        api.get("/wallet/transactions"),
        api.get("/automation/auto-pay-rules"),
        api.get("/security/settings"),
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data || []);
      setAutoPayRules(rulesRes.data || []);
      setSecuritySettings(settingsRes.data || {});
      setAutoTopUpForm({
        enabled: !!walletRes.data?.auto_topup_enabled,
        threshold: walletRes.data?.auto_topup_threshold ?? 1000,
        amount: walletRes.data?.auto_topup_amount ?? 5000,
      });
    } catch (err) {
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  };

  const balance = wallet?.balance ?? 0;
  const feePercent = wallet?.fee_percent ?? 4;

  const handleTopUp = async (e) => {
    e.preventDefault();
    const amount = parseFloat(topUpAmount);
    const amountCheck = validateWalletAmount(amount);
    if (!amountCheck.valid) {
      toast.error(amountCheck.message);
      return;
    }
    setTopUpProcessing(true);
    try {
      const createRes = await api.post("/wallet/add-funds", { amount, payment_method: "paychangu" });
      const txnId = createRes.data.transaction_id;
      const initRes = await api.post(`/wallet/add-funds/${txnId}/initiate`);
      const paymentUrl = initRes.data.payment_url || initRes.data.checkout_url;
      if (paymentUrl) {
        if (createRes.data.reference) {
          sessionStorage.setItem("pending_wallet_ref", createRes.data.reference);
        }
        toast.success("Redirecting to PayChangu…");
        window.location.href = paymentUrl;
      } else {
        toast.error("Could not start payment");
      }
    } catch (err) {
      toast.error(classifyError(err).message || "Top-up failed");
    } finally {
      setTopUpProcessing(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    const amount = parseFloat(transferForm.amount);
    const amountCheck = validateWalletAmount(amount);
    if (!amountCheck.valid) {
      toast.error(amountCheck.message);
      return;
    }
    if (!transferForm.phone.trim()) {
      toast.error("Enter recipient phone number");
      return;
    }
    setTransferProcessing(true);
    try {
      const res = await api.post("/wallet/transfer", {
        recipient_phone: transferForm.phone.trim(),
        amount,
        note: transferForm.note || null,
      });
      toast.success(`Sent MWK ${amount.toLocaleString()} to ${res.data.recipient}`);
      setShowTransfer(false);
      setTransferForm({ phone: "", amount: "", note: "" });
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Transfer failed");
    } finally {
      setTransferProcessing(false);
    }
  };

  const handleSaveAutoTopUp = async () => {
    setAutoTopUpSaving(true);
    try {
      await api.put("/wallet/auto-topup", {
        enabled: autoTopUpForm.enabled,
        threshold: autoTopUpForm.threshold,
        amount: autoTopUpForm.amount,
        payment_method: "paychangu",
      });
      toast.success("Auto top-up settings saved");
      setShowAutoTopUp(false);
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to save settings");
    } finally {
      setAutoTopUpSaving(false);
    }
  };

  const handleSetTransactionPin = async (e) => {
    e.preventDefault();
    if (txnPinForm.newPin.length < 4 || txnPinForm.newPin.length > 6) {
      toast.error("Payment PIN must be 4–6 digits");
      return;
    }
    if (txnPinForm.newPin !== txnPinForm.confirmPin) {
      toast.error("PINs do not match");
      return;
    }
    setTxnPinSaving(true);
    try {
      await api.post("/security/transaction-pin/set", {
        current_pin: txnPinForm.currentPin,
        pin: txnPinForm.newPin,
      });
      toast.success("Payment PIN set — you can now pay from wallet");
      setShowTxnPin(false);
      setTxnPinForm({ currentPin: "", newPin: "", confirmPin: "" });
      fetchData();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to set payment PIN");
    } finally {
      setTxnPinSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Your pocket wallet</h1>
          <p className="text-muted-foreground text-sm">Top up, send to family, and pay household bills from one balance</p>
        </div>
        <button onClick={fetchData} className="p-2 hover:bg-muted rounded-lg" aria-label="Refresh">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Balance card */}
      <div className="welcome-card-gradient rounded-2xl p-6 text-white relative overflow-hidden" data-testid="wallet-balance-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-white/80 text-sm font-medium">
            <Wallet className="w-4 h-4" /> SMARTPAY BALANCE
          </div>
          <button onClick={() => setHideBalance(!hideBalance)} className="p-1.5 hover:bg-white/10 rounded-lg">
            {hideBalance ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-4xl font-bold balance-display mb-2">
          {hideBalance ? "••••••" : `MWK ${balance.toLocaleString()}`}
        </p>
        <p className="text-white/70 text-sm">{feePercent}% platform fee on top-ups</p>
      </div>

      {/* PayChangu funding info */}
      {wallet?.funding && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-sm" data-testid="wallet-funding-info">
          <p className="font-medium text-blue-600 dark:text-blue-400 mb-1">Secured via PayChangu</p>
          <p className="text-muted-foreground">{wallet.funding.description}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => setShowTopUp(true)} className="flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90" data-testid="wallet-topup-btn">
          <Plus className="w-5 h-5" /> Top Up
        </button>
        <button onClick={() => setShowTransfer(true)} className="flex items-center justify-center gap-2 py-3 border border-border rounded-xl font-medium hover:bg-muted" data-testid="wallet-send-btn">
          <Send className="w-5 h-5" /> Send
        </button>
      </div>

      {/* Quick features */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button onClick={() => navigate("/buy")} className="p-4 bg-card border border-border/50 rounded-xl text-left hover:border-primary/50 transition-all">
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm">Pay Bills</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">Use wallet at checkout</p>
        </button>
        <button onClick={() => navigate("/smart")} className="p-4 bg-card border border-border/50 rounded-xl text-left hover:border-primary/50 transition-all">
          <div className="flex items-center gap-2 font-medium text-sm"><Sparkles className="w-4 h-4 text-amber-500" /> Auto-Pay</div>
          <p className="text-xs text-muted-foreground mt-1">{autoPayRules.filter(r => r.is_active).length} active rules</p>
        </button>
        <button onClick={() => navigate("/qr")} className="p-4 bg-card border border-border/50 rounded-xl text-left hover:border-primary/50 transition-all">
          <div className="flex items-center gap-2 font-medium text-sm"><QrCode className="w-4 h-4 text-primary" /> QR Pay</div>
          <p className="text-xs text-muted-foreground mt-1">Scan or generate codes</p>
        </button>
      </div>

      {/* Payment PIN */}
      {!securitySettings.transaction_pin_set && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-600 dark:text-amber-400">Set a payment PIN</p>
            <p className="text-sm text-muted-foreground mt-1">Required to pay bills from your wallet balance.</p>
            <button onClick={() => setShowTxnPin(true)} className="mt-2 text-sm text-primary hover:underline">Set payment PIN</button>
          </div>
        </div>
      )}

      {/* Auto top-up */}
      <div className="bg-card border border-border/50 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium">Auto Top-Up</p>
            <p className="text-sm text-muted-foreground">
              {wallet?.auto_topup_enabled
                ? `Top up MWK ${(wallet.auto_topup_amount || 0).toLocaleString()} when below MWK ${(wallet.auto_topup_threshold || 0).toLocaleString()}`
                : "Automatically add funds when balance is low"}
            </p>
          </div>
          <button onClick={() => setShowAutoTopUp(true)} className="text-sm text-primary hover:underline">Configure</button>
        </div>
      </div>

      {/* Auto-pay rules preview */}
      {autoPayRules.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Auto-Pay Rules</h2>
          <div className="space-y-2">
            {autoPayRules.slice(0, 3).map((rule) => (
              <div key={rule.id} className="flex items-center justify-between p-4 bg-card border border-border/50 rounded-xl">
                <div>
                  <p className="font-medium text-sm">{rule.rule_name}</p>
                  <p className="text-xs text-muted-foreground">{rule.provider_code} · MWK {(rule.payment_amount || 0).toLocaleString()}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${rule.is_active ? "bg-emerald-500/20 text-emerald-500" : "bg-muted text-muted-foreground"}`}>
                  {rule.is_active ? "Active" : "Paused"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transaction history */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Wallet Activity</h2>
        {transactions.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border/50 rounded-xl">
            <Wallet className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No wallet activity yet</p>
            <button onClick={() => setShowTopUp(true)} className="mt-3 text-primary hover:underline text-sm">Top up to get started</button>
          </div>
        ) : (
          <div className="bg-card border border-border/50 rounded-xl divide-y divide-border/50">
            {transactions.map((txn) => {
              const isCredit = txn.type === "credit";
              return (
                <div key={txn.id || txn.reference} className="flex items-center gap-4 p-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCredit ? "bg-emerald-500/20" : "bg-red-500/20"}`}>
                    {isCredit ? <ArrowDownLeft className="w-5 h-5 text-emerald-500" /> : <ArrowUpRight className="w-5 h-5 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{txn.description}</p>
                    <p className="text-xs text-muted-foreground">{txn.created_at ? new Date(txn.created_at).toLocaleString() : ""}</p>
                  </div>
                  <p className={`font-semibold text-sm ${isCredit ? "text-emerald-500" : ""}`}>
                    {isCredit ? "+" : "-"}MWK {(txn.amount || 0).toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top-up modal */}
      {showTopUp && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTopUp(false)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Top Up Wallet</h3>
              <button onClick={() => setShowTopUp(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Pay via Airtel Money, TNM Mpamba, or card through PayChangu. Funds are credited to your SmartPay balance.</p>
            <form onSubmit={handleTopUp} className="space-y-4">
              <input type="number" value={topUpAmount} onChange={(e) => setTopUpAmount(e.target.value)} placeholder="Amount (MWK)" min="100" step="100"
                className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl" data-testid="topup-amount-input" />
              <div className="flex flex-wrap gap-2">
                {QUICK_AMOUNTS.map((a) => (
                  <button key={a} type="button" onClick={() => setTopUpAmount(String(a))} className="px-3 py-1 text-sm bg-muted rounded-lg hover:bg-muted/80">
                    MWK {a.toLocaleString()}
                  </button>
                ))}
              </div>
              {topUpAmount && parseFloat(topUpAmount) >= 100 && (
                <p className="text-sm text-muted-foreground">
                  Total charge: MWK {(parseFloat(topUpAmount) * (1 + feePercent / 100)).toLocaleString()} (includes {feePercent}% fee)
                </p>
              )}
              <button type="submit" disabled={topUpProcessing} className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                {topUpProcessing ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : "Continue to PayChangu"}
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {/* Transfer modal */}
      {showTransfer && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTransfer(false)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Send Money</h3>
              <button onClick={() => setShowTransfer(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleTransfer} className="space-y-4">
              <input type="tel" value={transferForm.phone} onChange={(e) => setTransferForm(p => ({ ...p, phone: e.target.value }))} placeholder="Recipient phone (e.g. 0991234567)"
                className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl" data-testid="transfer-phone-input" />
              <input type="number" value={transferForm.amount} onChange={(e) => setTransferForm(p => ({ ...p, amount: e.target.value }))} placeholder="Amount (MWK)" min="100"
                className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl" data-testid="transfer-amount-input" />
              <input type="text" value={transferForm.note} onChange={(e) => setTransferForm(p => ({ ...p, note: e.target.value }))} placeholder="Note (optional)"
                className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl" />
              <button type="submit" disabled={transferProcessing} className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                {transferProcessing ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send"}
              </button>
            </form>
          </motion.div>
        </div>
      )}

      {/* Auto top-up modal */}
      {showAutoTopUp && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowAutoTopUp(false)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Auto Top-Up</h3>
              <button onClick={() => setShowAutoTopUp(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={autoTopUpForm.enabled} onChange={(e) => setAutoTopUpForm(p => ({ ...p, enabled: e.target.checked }))} className="w-4 h-4" />
                <span>Enable auto top-up</span>
              </label>
              <div>
                <label className="text-sm text-muted-foreground">When balance falls below (MWK)</label>
                <input type="number" value={autoTopUpForm.threshold} onChange={(e) => setAutoTopUpForm(p => ({ ...p, threshold: parseFloat(e.target.value) || 0 }))}
                  className="w-full mt-1 px-4 py-3 bg-input/50 border border-border rounded-xl" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Top up amount (MWK)</label>
                <input type="number" value={autoTopUpForm.amount} onChange={(e) => setAutoTopUpForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                  className="w-full mt-1 px-4 py-3 bg-input/50 border border-border rounded-xl" />
              </div>
              <button onClick={handleSaveAutoTopUp} disabled={autoTopUpSaving} className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
                {autoTopUpSaving ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Transaction PIN modal */}
      {showTxnPin && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowTxnPin(false)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-card border border-border rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Set Payment PIN</h3>
              <button onClick={() => setShowTxnPin(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSetTransactionPin} className="space-y-4">
              <input type="password" inputMode="numeric" maxLength={6} value={txnPinForm.currentPin} onChange={(e) => setTxnPinForm(p => ({ ...p, currentPin: e.target.value.replace(/\D/g, "") }))}
                placeholder="Current login PIN" className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl text-center tracking-widest" />
              <input type="password" inputMode="numeric" maxLength={6} value={txnPinForm.newPin} onChange={(e) => setTxnPinForm(p => ({ ...p, newPin: e.target.value.replace(/\D/g, "") }))}
                placeholder="New payment PIN (4–6 digits)" className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl text-center tracking-widest" />
              <input type="password" inputMode="numeric" maxLength={6} value={txnPinForm.confirmPin} onChange={(e) => setTxnPinForm(p => ({ ...p, confirmPin: e.target.value.replace(/\D/g, "") }))}
                placeholder="Confirm payment PIN" className="w-full px-4 py-3 bg-input/50 border border-border rounded-xl text-center tracking-widest" />
              <button type="submit" disabled={txnPinSaving} className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-medium disabled:opacity-50">
                {txnPinSaving ? "Saving…" : "Save Payment PIN"}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
